import { useMemo } from "react";

import { Box, ComponentWithAs, Heading, IconProps, List, Text, VStack } from "@chakra-ui/react";

import SidebarListItem from "@/components/shared/SidebarListItem";
import { NAV_MENU_WIDTH } from "@/constants/sizes";
import useReadySearchParams from "@/hooks/useReadySearchParams";
import { useWorkspace } from "@/providers/useWorkspace";

export interface SidebarMenuItem {
  label: string;
  icon: ComponentWithAs<"svg", IconProps>;
  slug: string;
  basePath?: string;
}

export type SidebarMenuProps<T> = {
  basePath?: string;
  heading?: string;
  width?: string | number;
  menuItems: ReadonlyArray<SidebarMenuItem | string>;
  selectedItem?: T;
};

function SidebarMenu<T extends string>({
  basePath,
  heading,
  width = NAV_MENU_WIDTH,
  menuItems,
  selectedItem,
}: SidebarMenuProps<T>) {
  const { currentWorkspace } = useWorkspace();
  // readySearchParams are the values of the search params in the current path.
  // If we're rendering /workspaces/[workspaceId]/applications then workspaceId will have a value
  const readySearchParams = useReadySearchParams();

  // There are paths, such as /profile, where we don't have workspaceId available from readySearchParams
  // but we need it to populate most SidebarListItem paths. For those cases we build a new object that
  // ensures we will have workspaceId available.
  const presentSearchParams = useMemo(() => {
    return {
      workspaceId: currentWorkspace?.databaseId ?? "",
      ...readySearchParams,
    } as typeof readySearchParams & { workspaceId: string };
  }, [readySearchParams, currentWorkspace]);

  return (
    <Box borderRightWidth="1px" h="full" minW="fit-content" overflow="hidden auto" width={width}>
      <VStack align="start" px={4} py={6} spacing={4}>
        {heading && (
          <Heading px={3} py={0} size="xs">
            {heading}
          </Heading>
        )}
        <List aria-label="navigation" as="nav" role="navigation" variant="navMenu" w="full">
          {basePath &&
            menuItems.map(item => {
              if (typeof item === "object") {
                const pathname = [item.basePath ?? basePath, item.slug].join("/");

                // Now that we're in the scope of an individual item and we have access to item.basePath
                // we need to eliminate extra query params that would be passed into SidebarListItem.href below.
                // For example if the current path is /workspaces/[workspaceId]/applications/[applicationId] but the
                // pathname is /workspaces/[workspaceId]/settings then we want to eliminate passing applicationId
                const query = Object.keys(presentSearchParams).reduce(
                  (memo: typeof presentSearchParams, param) => {
                    if (pathname?.match(new RegExp(`\\[${param}]`))) memo[param] = presentSearchParams[param];
                    return memo;
                  },
                  {} as typeof presentSearchParams & { workspaceId?: string }
                );

                return (
                  <SidebarListItem
                    key={item.label}
                    href={{ pathname, query }}
                    icon={item.icon}
                    isCurrent={item.slug === selectedItem}
                    label={item.label}
                  />
                );
              } else {
                return (
                  <Text key={item} fontSize="2xs" pb={1} pl={3} pt={7} textTransform="uppercase">
                    {item}
                  </Text>
                );
              }
            })}
        </List>
      </VStack>
    </Box>
  );
}

export default SidebarMenu;
